import React from 'react'
import {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import gsap from "gsap";
import SplitText from "gsap/SplitText";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(SplitText, ScrollTrigger);

const useIsomorphicLayoutEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

const AnimatedText = forwardRef(
  (
    {
      text = "",
      as: Tag = "p",
      splitType = "words", // chars | words | lines | combo | elements
      direction = "right",
      triggerOnce = true,
      scrollTrigger = true,
      start = "top 100%",
      stagger = 0.12,
      duration = 0.3,
      ease = "power3.out",
      delay = 0.1,
      animation = "default",
      shownOnLoad = false,
      className = "",
      style = {},
      children,
      ...props
    },
    parentRef
  ) => {
    const textRef = useRef(null);
    const splitInstance = useRef(null);
    const ctx = useRef(null);
    const animationRef = useRef(null);
    const scrollTriggerRef = useRef(null);
    const elementTargetsRef = useRef([]);
    const [hydrated, setHydrated] = useState(false);

    useEffect(() => {
      setHydrated(true);
    }, []);

    const cleanupSplit = () => {
      if (splitInstance.current) {
        splitInstance.current.revert();
        splitInstance.current = null;
      }
    };

    const cleanupAnimation = () => {
      if (animationRef.current) {
        animationRef.current.kill();
        animationRef.current = null;
      }

      if (scrollTriggerRef.current) {
        scrollTriggerRef.current.kill();
        scrollTriggerRef.current = null;
      }
    };

    const getActualSplitType = () => splitType;

    const getElementTargets = () => {
      if (!textRef.current) return [];
      return Array.from(textRef.current.children || []);
    };

    const getSplitTargets = () => {
      const actualSplitType = getActualSplitType();

      if (actualSplitType === "elements") {
        return elementTargetsRef.current.length
          ? elementTargetsRef.current
          : [textRef.current];
      }

      if (!splitInstance.current) return [textRef.current];

      if (actualSplitType.includes("chars")) return splitInstance.current.chars;
      if (actualSplitType.includes("lines")) return splitInstance.current.lines;
      if (actualSplitType.includes("words")) return splitInstance.current.words;

      return [textRef.current];
    };

    const buildTargets = () => {
      if (!textRef.current) return [];

      cleanupSplit();
      elementTargetsRef.current = [];

      const actualSplitType = getActualSplitType();

      if (actualSplitType === "combo") {
        return [textRef.current];
      }

      if (actualSplitType === "elements") {
        const childrenTargets = getElementTargets();
        elementTargetsRef.current = childrenTargets;

        return childrenTargets.length ? childrenTargets : [textRef.current];
      }

      splitInstance.current = new SplitText(textRef.current, {
        type: actualSplitType,
      });

      return getSplitTargets();
    };

    const getBaseFromVars = () => {
      const fromVars = { opacity: 0 };

      if (direction === "left") fromVars.x = -60;
      else if (direction === "right") fromVars.x = 60;
      else if (direction === "top") fromVars.y = -60;
      else if (direction === "bottom") fromVars.y = 60;
      else fromVars.x = 60;

      return fromVars;
    };

    const revealParent = () => {
      if (parentRef?.current) {
        gsap.set(parentRef.current, { opacity: 1 });
      }
    };

    const hideParent = () => {
      if (parentRef?.current) {
        gsap.set(parentRef.current, { opacity: 0 });
      }
    };

    const createAnimation = (targets) => {
      if (!targets.length) return null;

      const tl = gsap.timeline({
        paused: true,
        delay,
      });

      if (animation === "domino") {
        gsap.set(targets, {
          rotationZ: -90,
          transformOrigin: "bottom left",
          opacity: 0,
        });

        tl.to(targets, {
          rotationZ: 0,
          opacity: 1,
          stagger,
          duration,
          ease,
        });

        return tl;
      }

      if (animation === "flipBoard") {
        gsap.set(targets, {
          rotationX: -90,
          transformOrigin: "50% 0%",
          transformPerspective: 800,
          opacity: 0,
        });

        tl.to(targets, {
          rotationX: 0,
          opacity: 1,
          stagger,
          duration,
          ease,
        });

        return tl;
      }

      if (animation === "revealWipe") {
        gsap.set(targets, {
          clipPath: "inset(0 100% 0 0)",
          opacity: 1,
        });

        tl.to(targets, {
          clipPath: "inset(0 0% 0 0)",
          stagger,
          duration,
          ease: ease || "power3.inOut",
        });

        return tl;
      }

      if (animation === "fadeUpWords") {
        gsap.set(targets, {
          y: 40,
          opacity: 0,
        });

        tl.to(targets, {
          y: 0,
          opacity: 1,
          stagger,
          duration,
          ease,
        });

        return tl;
      }

      if (animation === "origamiUnfold") {
        gsap.set(targets, {
          rotationY: -90,
          rotationX: 45,
          transformOrigin: "left center",
          transformPerspective: 1000,
          opacity: 0,
        });

        tl.to(targets, {
          rotationY: 0,
          rotationX: 0,
          opacity: 1,
          stagger,
          duration,
          ease,
        });

        return tl;
      }

      const fromVars = getBaseFromVars();

      gsap.set(targets, {
        opacity: 0,
        x: fromVars.x || 0,
        y: fromVars.y || 0,
      });

      tl.to(targets, {
        opacity: 1,
        x: 0,
        y: 0,
        duration,
        ease,
        stagger: getActualSplitType() === "combo" ? 0 : stagger,
      });

      return tl;
    };

    const resetTargetsToInitialState = () => {
      const actualSplitType = getActualSplitType();

      const targets =
        actualSplitType === "elements"
          ? elementTargetsRef.current.length
            ? elementTargetsRef.current
            : [textRef.current]
          : splitInstance.current?.chars ||
            splitInstance.current?.words ||
            splitInstance.current?.lines ||
            [textRef.current];

      if (animation === "domino") {
        gsap.set(targets, {
          rotationZ: -90,
          transformOrigin: "bottom left",
          opacity: 0,
        });
        return;
      }

      if (animation === "flipBoard") {
        gsap.set(targets, {
          rotationX: -90,
          transformOrigin: "50% 0%",
          transformPerspective: 800,
          opacity: 0,
        });
        return;
      }

      if (animation === "revealWipe") {
        gsap.set(targets, {
          clipPath: "inset(0 100% 0 0)",
          opacity: 1,
        });
        return;
      }

      if (animation === "fadeUpWords") {
        gsap.set(targets, {
          y: 40,
          opacity: 0,
        });
        return;
      }

      if (animation === "origamiUnfold") {
        gsap.set(targets, {
          rotationY: -90,
          rotationX: 45,
          transformOrigin: "left center",
          transformPerspective: 1000,
          opacity: 0,
        });
        return;
      }

      const fromVars = getBaseFromVars();

      gsap.set(targets, {
        opacity: 0,
        x: fromVars.x || 0,
        y: fromVars.y || 0,
      });
    };

    const runAnimation = () => {
      if (!textRef.current) return;

      cleanupAnimation();

      const targets = buildTargets();
      const tl = createAnimation(targets);

      if (!tl) return;

      animationRef.current = tl;

      if (shownOnLoad || !scrollTrigger) {
        revealParent();
        tl.play(0);
        return;
      }

      scrollTriggerRef.current = ScrollTrigger.create({
        trigger: textRef.current,
        start,
        once: triggerOnce,
        markers: false,
        onEnter: () => {
          revealParent();
          tl.play(0);
        },
        onEnterBack: () => {
          if (!triggerOnce) {
            revealParent();
            tl.play(0);
          }
        },
        onLeaveBack: () => {
          if (!triggerOnce) {
            tl.pause(0);
            tl.progress(0);
            resetTargetsToInitialState();
          }
        },
      });
    };

    useIsomorphicLayoutEffect(() => {
      if (!hydrated || typeof window === "undefined") return undefined;

      ctx.current = gsap.context(() => {
        runAnimation();
      }, textRef);

      const handleRouteChange = () => {
        if (ctx.current) {
          ctx.current.revert();
        }

        cleanupAnimation();
        cleanupSplit();
        elementTargetsRef.current = [];
        hideParent();

        window.requestAnimationFrame(() => {
          runAnimation();
          ScrollTrigger.refresh();
        });
      };

      window.addEventListener("popstate", handleRouteChange);

      return () => {
        window.removeEventListener("popstate", handleRouteChange);

        if (ctx.current) {
          ctx.current.revert();
        }

        cleanupAnimation();
        cleanupSplit();
        elementTargetsRef.current = [];
      };
    }, [
      hydrated,
      splitType,
      direction,
      triggerOnce,
      scrollTrigger,
      start,
      stagger,
      duration,
      ease,
      delay,
      animation,
      shownOnLoad,
      text,
      children,
    ]);

    return (
      <Tag
        ref={textRef}
        className={`animated-text block w-full ${className}`}
        style={style}
        {...props}
      >
        {children || text}
      </Tag>
    );
  }
);

AnimatedText.displayName = "AnimatedText";

export default AnimatedText;